<template>
  <footer class="w-full relative mt-10 lg:mt-20">
    <div
      class="w-full py-5 lg:pt-10 lg:pb-6"
    :class="footerBgClass"
    >
      <div class="container">
        
        <!-- Contact & Logos Top Bar -->
        <div class="w-full flex flex-col lg:flex-row justify-between lg:items-center pb-6 lg:pb-10 mb-3 lg:mb-10 border-0 border-b border-solid" :class="footerBorderTopClass.replace('border-t-', 'border-')">
          <!-- Left: Phone Number Icon & Text -->
          <div class="flex items-center gap-4 lg:gap-6">
            <div class="flex items-center gap-3 lg:gap-4">
              <!-- Phone Icon (SVG) -->
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                class="w-7 h-7 lg:w-9 lg:h-9"
                :class="data?.color === 'black' ? 'fill-black' : 'fill-white'"
              >
                <path d="M19.95 21q-3.125 0-6.175-1.362t-5.55-3.863q-2.5-2.5-3.862-5.55T3 4.05q0-.45.3-.75t.75-.3H8.1q.35 0 .625.238t.325.562l.65 3.5q.05.4-.025.675T9.4 8.45L6.975 10.9q.5.925 1.187 1.787t1.513 1.663q.775.775 1.625 1.438T13.1 17l2.35-2.35q.225-.225.588-.337t.712-.063l3.45.7q.35.1.575.363T21 15.9v4.05q0 .45-.3.75t-.75.3Z"/>
              </svg>
              <a 
                :href="toHref(siteSetting?.phone, 'tel')"
                class="text-xl lg:text-3xl font-bold font-saira hover:opacity-80 duration-200 transition-all tracking-tight"
                :class="data?.color === 'black' ? 'text-black' : 'text-white'"
              >
                {{ siteSetting?.phone }}
              </a>
            </div>
          </div>
          
          <!-- Right: Partner/Company Logos -->
          <div class="hidden lg:flex items-center gap-6 lg:gap-10 mt-8 lg:mt-0 flex-wrap">
            <!-- Main Brand Logo Moved to Top -->
            <NuxtLink
              v-for="(logo, logoIndex) in (Array.isArray(siteSetting?.footerLogo) ? siteSetting.footerLogo : (siteSetting?.footerLogo ? [siteSetting.footerLogo] : []))"
              :key="`footer-logo-${logoIndex}`"
              :to="localePath('/')"
              class="flex items-center justify-center mr-0 lg:mr-4"
            >
              <img
                :src="`${baseUrl}${logo?.url}`"
                :alt="logo?.alternativeText ?? logo?.hash ?? logo?.caption"
                class="w-[90px] object-contain"
                width="90"
                height="90"
              />
            </NuxtLink>
            
          </div>
        </div>

        <div
          class="w-full flex flex-col gap-6 lg:gap-12 lg:flex-row lg:justify-between"
        >
          <!-- Navigation Menus -->
          <div
            v-if="!isMobile"
            class="w-full lg:w-[70%] hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 xl:gap-12 2xl:gap-16"
          >
            <template v-for="(item, index) in data?.mainItem">
              <div
                v-if="item.enabled"
                :key="`${index}-${item.id}`"
                class="w-full"
              >
                <div
                  class="pb-7 font-medium tracking-wide text-lg uppercase font-saira"
                  :class="data?.color === 'black' ? 'text-black' : 'text-white'"
                >
                  {{ item.title }}
                </div>
                <ul class="flex flex-col gap-y-4 opacity-90">
                  <template v-for="(subItem, subIndex) in item.item">
                    <li
                      v-if="subItem.enabled"
                      :key="`${subIndex}-${subItem.id}`"
                    >
                      <NuxtLink
                        :to="localePath(subItem.link)"
                        :aria-label="subItem.name"
                        :target="`_${subItem.target}`"
                        class="text-base tracking-wide hover:text-opacity-70 duration-200"
                        :class="footerItemColorClass"
                      >
                        {{ subItem.name }}
                      </NuxtLink>
                    </li>
                  </template>
                </ul>
              </div>
            </template>
          </div>
          <!-- Mobile Menu -->
          <div v-if="isMobile" class="w-full">
            <Accordion
              v-if="data?.mainItem"
              value="0"
              class="footer-accordion"
              :class="data?.color === 'black' ? 'black-theme' : ''"
            >
              <AccordionPanel
                v-for="(tab, index) in enabledTabs"
                :key="tab?.title + index"
                :value="tab?.title + index"
              >
                <AccordionHeader>{{ tab?.title }}</AccordionHeader>
                <AccordionContent>
                  <ul class="flex flex-col gap-y-4 opacity-90">
                    <template v-for="(subItem, subIndex) in tab?.item">
                      <li
                        v-if="subItem.enabled"
                        :key="`${subIndex}-${subItem.id}`"
                      >
                        <NuxtLink
                          :to="localePath(subItem.link)"
                          :aria-label="subItem.name"
                          :target="`_${subItem.target}`"
                          class="text-base hover:text-opacity-70 duration-200"
                          :class="footerItemColorClass"
                        >
                          {{ subItem.name }}
                        </NuxtLink>
                      </li>
                    </template>
                  </ul>
                </AccordionContent>
              </AccordionPanel>
            </Accordion>
          </div>
          <div
            class="w-full lg:w-1/5 relative lg:flex lg:flex-col lg:items-start"
          >
            <div
              class="pb-3 lg:pb-7 font-medium tracking-wide text-lg uppercase font-saira"
              :class="data?.color === 'black' ? 'text-black' : 'text-white'"
            >
              {{ $t("Contact") }}
            </div>
            <!-- İletişim -->
            <div class="w-full flex flex-col gap-4">
              <div v-if="footerAddress" class="flex flex-wrap items-baseline gap-x-2">
                <div class="text-base leading-relaxed">
                  <span
                    class="text-base font-medium"
                    :class="
                      data?.color === 'black' ? 'text-black' : 'text-white'
                    "
                  >
                    {{ $t("Address") }} :
                  </span>
                  <span
                    class="opacity-80"
                    :class="footerItemOpacityColorClass"
                  >
                    {{ footerAddress }}
                  </span>
                </div>
              </div>
              <div v-if="footerPhone" class="flex flex-wrap items-baseline gap-x-2">
                <span
                  class="text-base font-medium"
                  :class="data?.color === 'black' ? 'text-black' : 'text-white'"
                >
                  {{ $t("Phone") }} :
                </span>
                <a
                  :href="footerPhoneHref"
                  class="text-base hover:opacity-80 duration-200"
                  :class="
                    data?.color === 'black' ? 'text-black/80' : 'text-white/80'
                  "
                >
                  {{ footerPhone }}
                </a>
              </div>
              <div v-if="footerPhone" class="flex flex-wrap items-baseline gap-x-2">
                <span
                  class="text-base font-medium"
                  :class="data?.color === 'black' ? 'text-black' : 'text-white'"
                >
                  WhatsApp :
                </span>
                <div class="flex flex-col gap-2">
                  <a
                    v-if="footerSocialRows[0]?.href"
                    :href="footerSocialRows[0]?.href"
                    class="text-base hover:opacity-80 duration-200"
                    :class="footerItemOpacityColorClass"
                  >
                    {{ footerSocialRows[0]?.value }}
                  </a>
                  <span
                    v-else
                    class="text-base num-ltr"
                    :class="footerItemOpacityColorClass"
                  >
                    {{ footerSocialRows[0]?.value }}
                  </span>
                </div>
                <template v-for="(socialRow, index) in footerSocialRows.slice(1)">
                  <a
                    v-if="socialRow?.href"
                    :key="`social-link-${index}`"
                    :href="socialRow?.href"
                    class="text-base hover:opacity-80 duration-200"
                    :class="footerItemOpacityColorClass"
                  >
                    {{ socialRow?.value }}
                  </a>
                  <span
                    v-else
                    :key="`social-text-${index}`"
                    class="text-base num-ltr"
                    :class="footerItemOpacityColorClass"
                  >
                    {{ socialRow?.value }}
                  </span>
                </template>
              </div>
              <div v-if="footerEmail" class="flex flex-wrap items-baseline gap-x-2">
                <span
                  class="text-base font-medium"
                  :class="data?.color === 'black' ? 'text-black' : 'text-white'"
                >
                  {{ $t("Email") }} :
                </span>
                <a
                  :href="footerEmailHref"
                  class="text-base hover:opacity-80 duration-200"
                  :class="footerItemOpacityColorClass"
                >
                  {{ footerEmail }}
                </a>
              </div>
            </div>
            <!-- Store Logo -->
            <div
              v-if="hasAppStoreLinks"
              class="mt-6 lg:mt-10 flex flex-col gap-3 items-start"
            >
              <a
                v-if="hasAppleStoreLink"
                :href="siteSetting?.appStoreLinks?.appleStoreLink"
                target="_blank"
                rel="noopener noreferrer"
                class="flex border border-solid px-3 py-2 rounded-lg items-center gap-3 w-44"
                :class="
                  data?.color === 'black' ? 'border-black' : 'border-white'
                "
              >
                <div class="w-6 flex items-center">
                  <img
                    src="/icon/app-store.svg"
                    alt="App Store"
                    :class="data?.color === 'black' ? 'invert' : ''"
                    width="24"
                    height="24"
                  />
                </div>
                <div class="w-[calc(100%-24px)]">
                  <div
                    class="text-xs font-bold"
                    :class="
                      data?.color === 'black' ? 'text-black' : 'text-white'
                    "
                  >
                    {{ t("Store.AppStore") }}
                  </div>
                  <div
                    class="text-[10px] leading-none mt-0.5"
                    :class="
                      data?.color === 'black' ? 'text-black' : 'text-white'
                    "
                  >
                    {{ t("Store.GetApp") }}
                  </div>
                </div>
              </a>
              <a
                v-if="hasGooglePlayLink"
                :href="siteSetting?.appStoreLinks?.googlePlayLink"
                target="_blank"
                rel="noopener noreferrer"
                class="flex border border-solid px-3 py-2 rounded-lg items-center gap-3 w-44"
                :class="
                  data?.color === 'black' ? 'border-black' : 'border-white'
                "
              >
                <div class="w-6 flex items-center">
                  <img
                    src="/icon/google-play.svg"
                    alt="Play Store"
                    :class="data?.color === 'black' ? 'invert' : ''"
                    width="24"
                    height="24"
                  />
                </div>
                <div class="w-[calc(100%-24px)]">
                  <div
                    class="text-xs font-bold"
                    :class="
                      data?.color === 'black' ? 'text-black' : 'text-white'
                    "
                  >
                    {{ t("Store.GooglePlay") }}
                  </div>
                  <div
                    class="text-[10px] leading-none mt-0.5"
                    :class="
                      data?.color === 'black' ? 'text-black' : 'text-white'
                    "
                  >
                    {{ t("Store.GetApp") }}
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
        <div
          class="w-full mt-6 lg:mt-10 flex flex-col lg:flex-row lg:justify-between text-sm border-t border-solid border-r-0 border-l-0 border-b-0 pt-6  gap-4"
          :class="footerBorderTopClass"
        >
          <div class="flex flex-col gap-1">
            <span
              class="text-sm"
              :class="footerItemOpacity50ColorClass"
            >
              © {{ currentYear }} {{ siteSetting?.brandName }}.
              {{ $t("All Rights Reserved") }}</span
            >
            <span
              class="text-sm"
              :class="footerItemOpacity60ColorClass"
              >{{ $t("Site Credit Before") }}
              <a
                href="https://www.lucapixel.com"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold text-sm font-saira"
                :class="data?.color === 'black' ? 'text-black' : 'text-white'"
                title="Web Tasarım - Lucapixel"
                >LUCAPIXEL</a
              >
              {{ $t("Site Credit After") }}</span
            >
          </div>
          <div class="flex flex-col lg:items-end gap-4 ">
            <UiSocialMedia
              v-if="siteSetting?.footerSocialMedia"
              class="flex  lg:justify-end"
              :data="siteSetting?.socialMedia"
              :color="data?.color === 'black' ? 'text-black' : 'text-white'"
            />
          </div>
        </div>
      </div>
      <div
        v-if="siteSettingsStore.shape.footerShape"
        class="absolute -top-[18px] lg:-top-[38px] left-0 w-full overflow-hidden leading-none"
      >
        <!-- Shape -->
        <svg
          :class="['w-full h-5 lg:h-10 block', footerShapeFillClass]"
          preserveAspectRatio="none"
          width="100%"
          height="40"
          viewBox="0 0 1800 40"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 26.1652C0 26.1652 46.3491 19.8469 88.8889 19.4061C132.063 18.9653 168.889 19.4061 221.587 21.6102C293.333 24.5489 431.746 36.4509 505.397 29.9856C579.048 23.5203 582.222 23.2265 619.683 19.1122C661.587 14.4102 746.667 5.00616 852.063 5.44698C964.444 5.88779 1168.25 29.8387 1252.06 28.6632C1260.95 28.5162 1293.97 27.4877 1318.73 25.7244C1342.86 23.9612 1378.41 20.1408 1426.67 18.8183C1446.98 18.2306 1479.37 17.202 1516.83 17.4959C1526.35 17.6428 1556.83 18.0836 1593.02 19.8469C1629.21 21.6102 1662.86 24.402 1693.33 25.2836C1758.73 26.8999 1800 24.5489 1800 24.5489V40.4182H0V26.1652ZM144.127 23.8142C148.572 25.2836 156.825 24.402 171.429 24.402C186.032 24.402 210.159 26.1652 208.254 26.0183C206.349 25.8714 188.571 23.8142 170.159 23.2265C151.111 22.4918 140.318 22.3448 144.127 23.8142ZM260.952 27.7815C260.952 27.7815 276.19 28.0754 296.508 29.104C316.825 30.2795 361.905 33.2183 392.381 34.2468C444.444 35.8631 492.698 34.2468 488.254 34.0999C483.175 33.9529 427.302 34.9815 376.508 32.4836C355.556 31.455 317.46 29.2509 297.778 28.5162C276.825 27.6346 260.952 27.7815 260.952 27.7815ZM624.127 27.9285C613.333 28.9571 708.571 21.0224 741.587 18.9653C754.921 18.0836 780.952 16.6143 805.079 16.0265C829.841 15.4388 861.587 15.4388 860.952 15.2918C860.952 14.9979 824.127 14.1163 789.841 15.2918C755.556 16.4673 756.825 16.9081 741.587 17.6428C717.46 18.8183 634.921 26.8999 624.127 27.9285ZM535.873 30.2795C534.603 30.2795 540.952 30.8672 566.349 28.6632C591.746 26.4591 608.889 23.5203 610.159 23.2265C611.429 23.0795 587.937 25.8714 568.254 27.6346C549.206 29.3979 536.508 30.2795 535.873 30.2795ZM3.17456 31.6019C3.80945 31.455 15.238 29.9856 33.6506 27.9285C52.0634 25.8714 77.4603 23.8142 76.1904 23.9612C74.9207 23.9612 53.9683 24.8428 29.2064 27.6346C4.44446 30.4264 2.53967 31.7489 3.17456 31.6019ZM1601.27 23.3734C1600.63 23.6673 1613.33 23.6673 1629.84 25.1367C1646.35 26.606 1664.13 27.4877 1679.37 28.0754C1704.76 29.2509 1753.65 27.9285 1751.11 27.7815C1749.21 27.6346 1699.05 28.2224 1671.75 26.753C1657.78 26.0183 1656.51 26.0183 1635.56 24.402C1614.6 22.7857 1601.9 22.9326 1601.27 23.3734ZM1336.51 26.8999C1333.97 27.0469 1314.29 29.2509 1283.17 30.7203C1252.06 32.3366 1226.67 31.455 1226.67 31.8958C1226.67 32.3366 1233.02 33.2183 1266.03 32.3366C1299.05 31.455 1339.05 26.753 1336.51 26.8999ZM1345.4 30.5734C1344.13 30.4264 1323.81 32.4836 1309.21 33.0713C1294.6 33.5121 1297.14 33.806 1297.14 33.9529C1297.14 34.0999 1304.13 34.2468 1314.29 33.6591C1325.08 32.9244 1346.67 30.7203 1345.4 30.5734ZM1205.71 33.9529C1205.08 33.6591 1183.49 32.7774 1170.16 31.3081C1156.83 29.8387 1158.73 30.4264 1158.1 30.5734C1157.46 30.7203 1163.17 31.7489 1173.33 32.4836C1183.49 33.3652 1206.35 34.2468 1205.71 33.9529ZM1384.76 24.1081C1385.4 23.6673 1382.86 23.3734 1372.7 24.402C1362.54 25.4305 1356.19 26.3122 1358.73 26.1652C1361.27 26.0183 1364.44 25.5775 1374.6 24.9897C1385.4 24.402 1384.13 24.5489 1384.76 24.1081ZM1580.95 22.1979C1582.22 22.1979 1567.62 21.0224 1560 20.8755C1552.38 20.7285 1547.3 20.7285 1547.3 21.1693C1547.3 21.6102 1547.94 21.904 1557.46 21.7571C1567.62 21.6102 1579.68 22.1979 1580.95 22.1979ZM833.651 9.4143C834.286 9.26736 848.889 9.85512 881.27 9.85512C913.016 9.85512 967.619 13.3816 990.476 14.9979C1013.33 16.6143 926.984 9.26736 885.714 8.97349C844.444 8.67961 833.016 9.56124 833.651 9.4143Z"
          />
          <path
            d="M88.2538 13.9694C88.8888 14.1163 117.46 13.5285 133.968 13.9694C180.317 14.9979 241.905 18.6714 239.365 18.2306C236.825 17.7897 152.381 13.6755 131.429 13.3816C100.317 12.7938 87.6189 13.9694 88.2538 13.9694Z"
          />
          <path
            d="M356.19 26.0183C357.46 26.1652 384.762 27.4876 416.508 27.7815C448.254 28.0754 497.143 26.8999 494.603 27.0468C492.698 27.1938 457.143 29.6917 410.159 28.8101C363.175 27.9285 354.921 25.8713 356.19 26.0183Z"
          />
          <path
            d="M501.587 20.7285C502.222 20.8755 488.889 22.1979 481.27 22.3449C473.651 22.4918 468.571 22.3449 468.571 21.904C468.571 21.4632 469.206 21.1694 478.73 21.3163C489.524 21.4632 500.952 20.5816 501.587 20.7285Z"
          />
          <path
            d="M523.81 24.8428C523.81 24.9897 551.746 23.0795 563.81 21.6101C575.873 20.2877 603.175 17.0551 602.54 17.0551C601.905 16.9081 575.873 19.4061 562.54 20.8755C549.206 22.3448 523.81 24.6958 523.81 24.8428Z"
          />
          <path
            d="M819.048 1.18578C819.682 1.33272 848.254 0.744967 864.762 1.18578C911.111 2.21435 972.698 5.8878 970.159 5.44698C967.619 5.00617 883.175 0.891905 862.222 0.598029C831.111 0.0102768 818.413 1.03884 819.048 1.18578Z"
          />
          <path
            d="M1533.97 12.0592C1533.97 12.0592 1526.35 12.5 1515.56 12.2061C1504.76 11.9122 1478.73 10.8836 1463.49 11.1775C1436.83 11.7653 1416.51 15.1449 1419.05 14.9979C1421.59 14.9979 1446.35 11.7653 1473.65 12.2061C1485.08 12.353 1506.03 12.9408 1516.19 12.7938C1526.35 12.6469 1533.97 12.0592 1533.97 12.0592Z"
          />
          <path
            d="M1320.64 21.904C1319.37 21.7571 1307.94 23.0795 1293.33 23.5203C1278.73 23.8142 1280 23.9611 1280 24.255C1280 24.402 1291.43 24.8428 1301.59 24.255C1311.75 23.5203 1321.9 22.051 1320.64 21.904Z"
          />
          <path
            d="M1700.95 21.0224C1700.95 21.1693 1673.65 21.0224 1658.41 20.2877C1643.17 19.553 1626.67 17.4959 1626.67 17.349C1626.67 17.202 1643.81 18.5245 1659.05 19.4061C1673.65 20.2877 1700.32 20.8755 1700.95 21.0224Z"
          />
        </svg>
        <!-- Shape -->
      </div>
    </div>
  </footer>
</template>
<script setup>
import { computed } from "vue";
import { useLocalePath } from "#i18n";
import { useIsMobile } from "~/composables/utils";
import { useI18n } from "vue-i18n";
import { useSiteSettingsStore } from "@/store/index";
const { t, locale } = useI18n();
const siteSettingsStore = useSiteSettingsStore();
const props = defineProps({
  data: {
    type: Object,
  },
  siteSetting: {
    type: Object,
  },
});

const localePath = useLocalePath();

const footerBgClass = computed(() => {
  const bg = props.data?.backgroundColor;
  if (bg === 'first') return 'bg-brand-first';
  if (bg === 'second') return 'bg-brand-second';
  if (bg === 'third') return 'bg-brand-third';
  if (bg === 'dark-first') return 'bg-brand-dark';
  if (bg === 'dark-second') return 'bg-brand-dark-second';
  if (bg === 'light-first') return 'bg-brand-light';
  if (bg === 'light-second') return 'bg-brand-light-second';
  return 'bg-brand-dark';
});

const footerItemColorClass = computed(() => props.data?.color === 'black' ? 'text-black' : 'text-white');
const footerItemOpacityColorClass = computed(() => props.data?.color === 'black' ? 'text-black/80' : 'text-white/80');
const footerItemOpacity50ColorClass = computed(() => props.data?.color === 'black' ? 'text-black/50' : 'text-white/50');
const footerItemOpacity60ColorClass = computed(() => props.data?.color === 'black' ? 'text-black/60' : 'text-white/60');
const footerBorderClass = computed(() => props.data?.color === 'black' ? 'border-black' : 'border-white');

const hasAppleStoreLink = computed(() => props.siteSetting?.appStoreLinks?.appleStoreLink?.length > 0 && props.siteSetting?.appStoreLinks?.appleStoreEnabled);
const hasGooglePlayLink = computed(() => props.siteSetting?.appStoreLinks?.googlePlayLink?.length > 0 && props.siteSetting?.appStoreLinks?.googlePlayEnabled);
const hasAppStoreLinks = computed(() => hasAppleStoreLink.value || hasGooglePlayLink.value);

const footerBorderTopClass = computed(() => {
  const bg = props.data?.backgroundColor;
  if (bg === 'first' || bg === 'second' || bg === 'dark-first' || bg === 'dark-second') return 'border-t-white/20';
  if (bg === 'third' || bg === 'light-first' || bg === 'light-second') return 'border-t-black/20';
  return '';
});

const footerShapeFillClass = computed(() => {
  const bg = props.data?.backgroundColor;
  if (bg === 'first') return 'fill-brand-first';
  if (bg === 'second') return 'fill-brand-second';
  if (bg === 'third') return 'fill-brand-third';
  if (bg === 'dark-first') return 'fill-brand-dark';
  if (bg === 'dark-second') return 'fill-brand-dark-second';
  return 'fill-brand-dark';
});

const { isMobile } = useIsMobile();
const currentYear = new Date().getFullYear();

const runtimeConfig = useRuntimeConfig();
const baseUrl = runtimeConfig.public.baseUrl;

const contactSource = computed(
  () => siteSettingsStore.footerContact || {},
);

const readContactValue = (...keys) => {
  for (const key of keys) {
    const value = contactSource.value?.[key];
    if (typeof value === "string" && value.trim().length) {
      return value.trim();
    }
  }
  return "";
};

const footerAddress = computed(() => readContactValue("address"));
const footerPhone = computed(() => readContactValue("phone"));
const footerEmail = computed(() => readContactValue("email", "mail"));
const footerWhatsapp = computed(() => readContactValue("whatsapp"));
const footerViber = computed(() => readContactValue("viber"));
const footerWechat = computed(() => readContactValue("wechat"));

const toDigits = (value) => String(value || "").replace(/\D+/g, "");
const toInternationalDigits = (value) => {
  const digits = toDigits(value);
  if (!digits) return "";
  if (digits.startsWith("90")) return digits;
  if (digits.startsWith("0")) return `9${digits}`;
  if (digits.startsWith("9")) return digits;
  return `90${digits}`;
};
const toHref = (value, type = "tel") => {
  if (!value) return null;
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  if (type === "mailto") {
    return `mailto:${value}`;
  }
  const digits = toInternationalDigits(value);
  return digits ? `tel:+${digits}` : null;
};

const footerPhoneHref = computed(() => toHref(footerPhone.value, "tel"));
const footerEmailHref = computed(() => toHref(footerEmail.value, "mailto"));
const footerSocialRows = computed(() => {
  const rows = [];
  if (footerWhatsapp.value) {
    const digits = toInternationalDigits(footerWhatsapp.value);
    rows.push({
      value: footerWhatsapp.value,
      href: digits ? `https://wa.me/${digits}` : toHref(footerWhatsapp.value),
    });
  }
  if (footerViber.value) {
    rows.push({ value: footerViber.value, href: toHref(footerViber.value) });
  }
  if (footerWechat.value) {
    const digits = toInternationalDigits(footerWechat.value);
    rows.push({
      value: footerWechat.value,
      href: digits ? `https://wa.me/${digits}` : null,
    });
  }
  return rows;
});

// Tab Data Enabled
const enabledTabs = computed(() =>
  props.data?.mainItem?.filter((tab) => tab.enabled),
);
</script>
